import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useCostCenters } from "@/hooks/use-cost-centers";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssignmentFormSheet, AssignmentFormData } from "@/components/fleet/AssignmentFormSheet";
import { DataTable } from "@/components/admin/data-table";
import { Badge } from "@/components/ui/badge";
import { Users, Car, Building2, Plus, Edit2, Trash2, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ColumnDef } from "@tanstack/react-table";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";

interface Assignment {
  id: string;
  vehicle_id: string;
  driver_id: string;
  starts_at: string;
  ends_at: string | null;
  cost_center_id: string | null;
  status: string;
  created_at: string;
  vehicle?: { plate: string; make: string; model: string } | null;
  driver?: { name: string } | null;
  cost_center?: { id: string; name: string; code?: string } | null;
}

export default function FleetAssignments() {
  const { profile, companies, activeCompany, session, hasCompanyRole } = useAuth();
  const { data: costCenters } = useCostCenters();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { canCreate, canEdit, canDelete, isViewer } = useFleetPermissions();
  const [isFormSheetOpen, setIsFormSheetOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
  const [formData, setFormData] = useState({
    vehicle_id: "",
    driver_id: "",
    starts_at: "",
    ends_at: "",
    cost_center_id: "",
    status: "active",
  });

  const currentCompanyId = activeCompany?.id || companies?.[0]?.id;

  // Fetch assignments
  const { data: assignments, isLoading } = useQuery({
    queryKey: ["fleetAssignments", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_assignments")
        .select(`
          *,
          vehicle:fleet_vehicles(plate, make, model),
          driver:fleet_drivers(name)
        `)
        .eq("company_id", currentCompanyId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Assignment[];
    },
    enabled: !!currentCompanyId,
  });

  // Fetch vehicles for dropdown
  const { data: vehicles } = useQuery({
    queryKey: ["fleetVehicles", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_vehicles")
        .select("id, plate, make, model")
        .eq("company_id", currentCompanyId)
        .eq("status", "active");

      if (error) throw error;
      return data;
    },
    enabled: !!currentCompanyId,
  });

  // Fetch drivers for dropdown
  const { data: drivers } = useQuery({
    queryKey: ["fleetDrivers", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fleet_drivers")
        .select("id, name")
        .eq("company_id", currentCompanyId)
        .eq("status", "active");

      if (error) throw error;
      return data;
    },
    enabled: !!currentCompanyId,
  });

  // Create assignment mutation
  const createAssignmentMutation = useMutation({
    mutationFn: async (data: any) => {
      // Verificar se o usuário está autenticado
      if (!session?.user?.id) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      if (!currentCompanyId) {
        throw new Error("Empresa não identificada.");
      }

      // Processar as datas e sanitizar dados
      const processedData = {
        ...data,
        cost_center_id: (data.cost_center_id && data.cost_center_id !== 'none') ? data.cost_center_id : null, // Convert empty string or 'none' to null
        starts_at: data.starts_at ? new Date(data.starts_at + 'T00:00:00.000Z').toISOString() : null,
        ends_at: data.ends_at ? new Date(data.ends_at + 'T23:59:59.999Z').toISOString() : null,
        company_id: currentCompanyId,
        created_by: session.user.id
      };

      const { error } = await supabase
        .from("fleet_assignments")
        .insert([processedData]);

      if (error) {
        console.error("Erro na criação de alocação:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetAssignments"] });
      setIsFormSheetOpen(false);
      resetForm();
      toast({
        title: "Sucesso",
        description: "Alocação criada com sucesso.",
      });
    },
    onError: (error: any) => {
      console.error("Erro na criação de alocação:", error);
      let errorMessage = "Erro ao criar alocação. Verifique sua conexão e tente novamente.";
      
      if (error.code === "22P02") {
        errorMessage = "Erro de validação: verifique se todos os campos obrigatórios estão preenchidos corretamente.";
      }
      
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || errorMessage,
      });
    },
  });

  // Update assignment mutation
  const updateAssignmentMutation = useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      if (!session?.user?.id) {
        throw new Error("Usuário não autenticado. Faça login novamente.");
      }

      // Processar as datas e sanitizar dados
      const processedData = {
        ...data,
        cost_center_id: (data.cost_center_id && data.cost_center_id !== 'none') ? data.cost_center_id : null, // Convert empty string or 'none' to null
        starts_at: data.starts_at ? new Date(data.starts_at + 'T00:00:00.000Z').toISOString() : null,
        ends_at: data.ends_at ? new Date(data.ends_at + 'T23:59:59.999Z').toISOString() : null,
        updated_by: session.user.id,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from("fleet_assignments")
        .update(processedData)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetAssignments"] });
      setIsFormSheetOpen(false);
      setEditingAssignment(null);
      resetForm();
      toast({
        title: "Sucesso",
        description: "Alocação atualizada com sucesso.",
      });
    },
    onError: (error: any) => {
      let errorMessage = "Erro ao atualizar alocação.";
      
      if (error.code === "22P02") {
        errorMessage = "Erro de validação: verifique se todos os campos obrigatórios estão preenchidos corretamente.";
      }
      
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || errorMessage,
      });
    },
  });

  // Delete assignment mutation
  const deleteAssignmentMutation = useMutation({
    mutationFn: async (id: string) => {
      // Verificar permissão primeiro
      if (!hasCompanyRole(activeCompany?.id, 'company_admin')) {
        throw new Error('Você não tem permissão para excluir alocações. Apenas administradores da empresa podem realizar esta ação.');
      }

      const { error, count } = await supabase
        .from("fleet_assignments")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      if (count === 0) {
        throw new Error('Alocação não encontrada ou já foi removida.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fleetAssignments"] });
      toast({
        title: "Sucesso",
        description: "Alocação removida com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao remover alocação",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const resetForm = () => {
    setFormData({
      vehicle_id: "",
      driver_id: "",
      starts_at: "",
      ends_at: "",
      cost_center_id: "none",
      status: "active",
    });
  };

  const handleSubmit = (data: AssignmentFormData) => {
    // Validações básicas
    if (!data.vehicle_id || !data.driver_id || !data.starts_at) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Preencha todos os campos obrigatórios.",
      });
      return;
    }

    // Verificar se o usuário está autenticado antes de submeter
    if (!session?.user?.id) {
      toast({
        variant: "destructive",
        title: "Erro de Autenticação",
        description: "Sessão expirada. Faça login novamente.",
      });
      return;
    }
    
    const processedData = {
      ...data,
      cost_center_id: (data.cost_center_id && data.cost_center_id !== 'none') ? data.cost_center_id : null,
      starts_at: data.starts_at ? new Date(data.starts_at + 'T00:00:00.000Z').toISOString() : '',
      ends_at: data.ends_at ? new Date(data.ends_at + 'T23:59:59.999Z').toISOString() : null,
    };
    
    if (editingAssignment) {
      updateAssignmentMutation.mutate({ id: editingAssignment.id, ...processedData });
    } else {
      createAssignmentMutation.mutate(processedData);
    }
  };

  const handleEdit = (assignment: Assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      vehicle_id: assignment.vehicle_id,
      driver_id: assignment.driver_id,
      starts_at: assignment.starts_at?.split('T')[0] || "",
      ends_at: assignment.ends_at?.split('T')[0] || "",
      cost_center_id: assignment.cost_center_id || "none",
      status: assignment.status,
    });
    setIsFormSheetOpen(true);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge variant="default">Ativo</Badge>;
      case "completed":
        return <Badge variant="secondary">Finalizado</Badge>;
      case "cancelled":
        return <Badge variant="destructive">Cancelado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Indefinido";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const columns: ColumnDef<Assignment>[] = [
    {
      accessorKey: "vehicle.plate",
      header: "Veículo",
      cell: ({ row }) => {
        const vehicle = row.original.vehicle;
        return vehicle ? `${vehicle.plate} - ${vehicle.make} ${vehicle.model}` : "N/A";
      },
    },
    {
      accessorKey: "driver.name",
      header: "Motorista",
    },
    {
      accessorKey: "cost_center_id",
      header: "Centro de Custo",
      cell: ({ row }) => {
        const costCenterId = row.getValue("cost_center_id") as string;
        if (!costCenterId) return "-";
        
        const costCenter = costCenters?.find(cc => cc.id === costCenterId);
        return costCenter ? `${costCenter.name}${costCenter.code ? ` (${costCenter.code})` : ''}` : costCenterId;
      },
    },
    {
      accessorKey: "starts_at",
      header: "Início",
      cell: ({ row }) => formatDate(row.getValue("starts_at")),
    },
    {
      accessorKey: "ends_at",
      header: "Fim",
      cell: ({ row }) => formatDate(row.getValue("ends_at")),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getStatusBadge(row.getValue("status")),
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(row.original)}
            disabled={!canEdit}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteAssignmentMutation.mutate(row.original.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (!currentCompanyId) {
    return (
      <div className="p-6">
        <p>Nenhuma empresa associada encontrada.</p>
      </div>
    );
  }

  const activeAssignments = assignments?.filter(a => a.status === "active")?.length || 0;
  const completedAssignments = assignments?.filter(a => a.status === "completed")?.length || 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Alocações</h1>
          <p className="text-muted-foreground">Gerencie alocações de veículos e motoristas</p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button 
            onClick={() => { 
              setEditingAssignment(null); 
              resetForm(); 
              setIsFormSheetOpen(true);
            }} 
            disabled={!canCreate}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nova Alocação
          </Button>

          <AssignmentFormSheet
            open={isFormSheetOpen}
            onOpenChange={setIsFormSheetOpen}
            assignment={editingAssignment}
            onSubmit={handleSubmit}
            isLoading={createAssignmentMutation.isPending || updateAssignmentMutation.isPending}
            vehicles={vehicles}
            drivers={drivers}
            costCenters={costCenters}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Alocações</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{assignments?.length || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alocações Ativas</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeAssignments}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Finalizadas</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedAssignments}</div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Alocações</CardTitle>
          <CardDescription>
            Lista de todas as alocações de veículos e motoristas
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={assignments || []}
            searchKey="vehicle"
          />
        </CardContent>
      </Card>
    </div>
  );
}